import React, { useContext, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import Title from "../components/header/Title";
import AuthContext from "../store/auth-context";
import MediaService from "../services/MediaService";
import { getErrorMessage, resolveImageUrl, unwrapData } from "../services/apiUtils";

const formatRoleName = (roleName?: string): string => {
  if (!roleName) return "User";
  return roleName
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const MAX_FILE_SIZE_MB = 5;

const Profile: React.FC = () => {
  const { user, updateAvatar } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handlePickPhoto = () => fileInputRef.current?.click();

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user) return;

    if (!file.type.startsWith("image/")) {
      setSnackbar({ open: true, message: "Please choose an image file.", severity: "error" });
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: `Image is too large. Please choose one under ${MAX_FILE_SIZE_MB}MB.`,
        severity: "error",
      });
      return;
    }

    setUploading(true);
    MediaService.uploadSingle(file, user.userId)
      .then((res: any) => {
        const media = unwrapData(res);
        const url = media?.col1 || media?.dataUrl;
        if (!url) {
          throw new Error("Upload succeeded but no image URL was returned.");
        }
        updateAvatar(url);
        setSnackbar({ open: true, message: "Profile photo updated.", severity: "success" });
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not upload the photo. Please try again."),
          severity: "error",
        });
      })
      .finally(() => setUploading(false));
  };

  if (!user) return null;

  const infoRows: Array<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = [
    { icon: <EmailOutlinedIcon fontSize="small" />, label: "Email", value: user.email || "-" },
    { icon: <BadgeOutlinedIcon fontSize="small" />, label: "User ID", value: user.userId },
    {
      icon: <PublicRoundedIcon fontSize="small" />,
      label: "Country",
      value: user.country ? user.country.toUpperCase() : "-",
    },
    {
      icon: <VerifiedUserOutlinedIcon fontSize="small" />,
      label: "Role",
      value: formatRoleName(user.roleName),
    },
  ];

  return (
    <React.Fragment>
      <Title title="Profile" subtitle="Your account details" />

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(16,24,40,0.05)",
          overflow: "hidden",
          maxWidth: 980,
        }}
      >
        {/* Cover */}
        <Box
          sx={{
            height: 128,
            background:
              "radial-gradient(circle at 85% 20%, rgba(246,130,31,0.55) 0, rgba(246,130,31,0) 45%), linear-gradient(120deg, #1F2266 0%, #303398 55%, #4F55D6 100%)",
          }}
        />

        <Box sx={{ px: { xs: 3, sm: 4 }, pb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-end" },
              gap: { xs: 1.5, sm: 3 },
              mt: "-52px",
              mb: 3,
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
              <Avatar
                src={resolveImageUrl(user.avatarUrl) || undefined}
                alt={user.name || "User"}
                sx={{
                  width: 112,
                  height: 112,
                  fontSize: 40,
                  fontWeight: 700,
                  bgcolor: "primary.light",
                  color: "primary.main",
                  border: "4px solid #fff",
                  boxShadow: "0 4px 14px rgba(15,23,42,0.12)",
                }}
              >
                {(user.name || "U").charAt(0).toUpperCase()}
              </Avatar>

              {uploading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.7)",
                    borderRadius: "50%",
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}

              <Tooltip title="Change photo">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handlePickPhoto}
                    disabled={uploading}
                    aria-label="Change profile photo"
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      minWidth: 0,
                      width: 36,
                      height: 36,
                      p: 0,
                      borderRadius: "50%",
                      border: "3px solid #fff",
                    }}
                  >
                    <PhotoCameraRoundedIcon sx={{ fontSize: 17 }} />
                  </Button>
                </span>
              </Tooltip>

              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelected} />
            </Box>

            <Box sx={{ flexGrow: 1, textAlign: { xs: "center", sm: "left" }, pb: { sm: 0.5 } }}>
              <Typography variant="h5" fontWeight={800}>
                {user.name || "User"}
              </Typography>
              <Chip
                size="small"
                label={formatRoleName(user.roleName)}
                color="secondary"
                variant="outlined"
                sx={{ mt: 0.75 }}
              />
            </Box>

            <Button
              variant="outlined"
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => navigate("/settings")}
              sx={{ mb: { sm: 0.5 } }}
            >
              Account settings
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            {infoRows.map((row) => (
              <Grid item xs={12} sm={6} key={row.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "#F8FAFC",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "primary.light",
                      color: "primary.main",
                      flexShrink: 0,
                    }}
                  >
                    {row.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {row.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>
            Use the camera button to change your profile photo. Recommended: a square image under{" "}
            {MAX_FILE_SIZE_MB}MB.
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default Profile;
